import stringify from 'json-stable-stringify';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {assert} from 'Asserts';
import {
  StructuredTable,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {MethodComponent} from 'procedures/ProcedureDomainTypes';

type DependentProvenanceToDelegatedProvenance = Record<string, string>;
type DelegateColumnsByDataType = Record<string, DependentProvenanceToDelegatedProvenance>;

export type DelegateInstaller<TTableMetadata, TRowMetadata> = (
  table: StructuredTable<TTableMetadata, TRowMetadata>,
  row: StructuredTableRow<TRowMetadata>,
  rowIndex: number
) => StructuredTableRow<TRowMetadata>;

interface RowDelegateControllerInputs<T extends MethodComponent, TTableMetadata, TRowMetadata, TDataType> {
  /**
   * Row type tracking
   */
  // Has the user has selected a data type for this row?
  getDataTypeByIndex: (detailIndex: number) => NonNullable<TDataType>;

  // A list of column IDs which should delegate based on the dataType for this row.
  // Set to "ALL_DELEGATE" to apply to all ROW_DELEGATE columns in the table.
  dependentColumnProvenanceIds: Array<string> | 'ALL_DELEGATE';

  /**
   * Table management
   */
  details: Array<T>;
  // The table will be recreated with every change
  createTable: (
    installDelegatesIntoRow: DelegateInstaller<TTableMetadata, TRowMetadata>
  ) => StructuredTable<TTableMetadata, TRowMetadata>;

  getDelegateColumnByColumnAndDataType: (
    dependentColumn: StructuredTableColumn,
    dataType: TDataType,
    tagSchemas?: Array<{id: string}>
  ) => StructuredTableColumn;

  tagSchemas?: Array<{id: string}>;
}

interface RowDelegateControllerOutputs<T extends MethodComponent, TTableMetadata, TRowMetadata> {
  // We pass back a traditional `value/onChange` pair
  table: StructuredTable<TTableMetadata, TRowMetadata>;
  onChangeDetails: (newDetails: Array<T>) => void;
}

/**
 * This hook allows configuration tables to use a single "type" column as a trigger to cause
 * one or more other columns in the table to use a row delegate.
 *
 * E.g., for data parameters, the user first selects TYPE from [float, text]. This causes the
 * table cells in several other columns (default value, min, max) to use a cell editor to match,
 * including validation.
 */
export default function useRowDelegateController<
  T extends MethodComponent,
  TTableMetadata,
  TRowMetadata,
  TDataType extends object,
>(
  params: RowDelegateControllerInputs<T, TTableMetadata, TRowMetadata, TDataType>
): RowDelegateControllerOutputs<T, TTableMetadata, TRowMetadata> {
  const [tableRecreationTrigger, setTableRecreationTrigger] = useState(0);
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const numberOfErrors = procedureValidationErrors.length;

  const delegateColumns = useRef<Record<string, StructuredTableColumn>>({});
  const delegateColumnsByDataType = useRef<DelegateColumnsByDataType>({});

  const findDependentColumns = (
    t: StructuredTable<TTableMetadata, TRowMetadata>
  ): Array<StructuredTableColumn> => {
    if (params.dependentColumnProvenanceIds === 'ALL_DELEGATE') {
      return t.columns.filter((col) => col.columnType === TableColumnType.ROW_DELEGATE);
    } else {
      return t.columns.filter((col) => params.dependentColumnProvenanceIds.includes(col.provenanceUuid));
    }
  };

  const addDelegateType = (
    table: StructuredTable<TTableMetadata, TRowMetadata>,
    dataType: TDataType
  ): DependentProvenanceToDelegatedProvenance => {
    const dataTypeHash = stringify(dataType);
    const dependentColumns = findDependentColumns(table);

    for (const dependentColumn of dependentColumns) {
      // Ok we are looking at all relevant ROW_DELEGATE columns
      const delegateColumn = params.getDelegateColumnByColumnAndDataType(
        dependentColumn,
        dataType,
        params.tagSchemas ?? []
      );

      delegateColumns.current[delegateColumn.provenanceUuid] = delegateColumn;

      // Add the new delegate column into our local cache
      if (delegateColumnsByDataType.current[dataTypeHash] == null) {
        delegateColumnsByDataType.current[dataTypeHash] = {
          [dependentColumn.provenanceUuid]: delegateColumn.provenanceUuid,
        };
      } else {
        delegateColumnsByDataType.current[dataTypeHash][dependentColumn.provenanceUuid] =
          delegateColumn.provenanceUuid;
      }
    }
    return delegateColumnsByDataType.current[dataTypeHash];
  };

  const installDelegateIntoRow = (
    table: StructuredTable<TTableMetadata, TRowMetadata>,
    row: StructuredTableRow<TRowMetadata>,
    rowIndex: number
  ): StructuredTableRow<TRowMetadata> => {
    const dataType = params.getDataTypeByIndex(rowIndex);
    assert(dataType != null, 'Unexpected null dataType', {row});
    const dataTypeHash = stringify(dataType);
    let delegatedColumnsForThisType = delegateColumnsByDataType.current[dataTypeHash];

    if (delegatedColumnsForThisType == null) {
      // The trigger column has a new type which wasn't previously accounted for
      delegatedColumnsForThisType = addDelegateType(table, dataType);
    }

    if (delegatedColumnsForThisType != null) {
      return {
        ...row,
        metadata: {...row.metadata, delegatedColumns: delegatedColumnsForThisType},
      };
    } else {
      return row;
    }
  };

  useEffect(() => {
    // Clearing the cached delegate column info when tagSchema is set to recreate delegate column with
    // correct schema filtering. This ensures that getDelegateColumnByColumnAndDataType is called with
    // fetched tagSchema list.
    delegateColumnsByDataType.current = {};
  }, [params.tagSchemas]);

  /**
   * The table needs to be recreated in response to a few triggers:
   *   1. Via onChangeDetails, below, which is fired by the component when the delegate-trigger column changes
   *   2. Whenever the global error count changes. We do cross-table error tracking, so something like a duplicate
   *       name will display as an error in multiple tables. When the error is removed, we need to rebuild all of them.
   *   3. When the `details` change. This will happen during a save, and requires a refresh of the table.
   *   4. When tagSchema list is fetched from query, the delegate column schema filtering needs to be set correctly.
   */
  const _table = useMemo(
    () => {
      const t = params.createTable(installDelegateIntoRow);
      return {
        ...t,
        delegatedColumns: Object.values(delegateColumns.current),
      };
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [tableRecreationTrigger, params.details, numberOfErrors, params.tagSchemas]
  );

  // Any change in the table will trigger a rebuild
  const onChangeDetails = useCallback((): void => {
    setTableRecreationTrigger((tr) => tr + 1);
  }, []);

  return {
    onChangeDetails,
    table: _table,
  };
}
